import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useMentorProfileReviewsQueue } from "@/lib/api/mentor-profile";
import {
  useStoryReviewQueue,
  type StoryReview,
} from "@/lib/api/mentor-story-review";
import { useUser } from "@/lib/api/user";
import { useAbility } from "@/lib/casl/ability-context";
import { useMentorStoriesEnabled } from "@/lib/hooks/use-mentor-stories";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { getPreferredName } from "@/lib/redacted-display";
import { cn, getInitials } from "@/lib/utils";
import { Button } from "@somethings-ui/web";
import { Link } from "@tanstack/react-router";
import { ChevronDown, Loader2 } from "lucide-react";
import { DateTime } from "luxon";
import { useState } from "react";

function mentorLabel(review: {
  mentor?: {
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  };
  draft: { display_name?: string; first_name?: string; last_name?: string };
}) {
  const fromMentor = [review.mentor?.first_name, review.mentor?.last_name]
    .filter(Boolean)
    .join(" ");
  if (fromMentor) return fromMentor;
  const fromDraft =
    review.draft.display_name?.trim() ||
    [review.draft.first_name, review.draft.last_name].filter(Boolean).join(" ");
  return fromDraft || review.mentor?.email || "Mentor";
}

function mentorAvatarUrl(review: {
  mentor?: { profile_picture_url?: string | null };
  draft: {
    match_card_photo_url?: string;
    profile_picture_url?: string;
  };
}) {
  return (
    review.mentor?.profile_picture_url ||
    review.draft.profile_picture_url ||
    review.draft.match_card_photo_url ||
    null
  );
}

function formatSubmitted(iso: string | null | undefined) {
  if (!iso) return null;
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return null;
  return `submitted ${dt.toFormat("M/d/yy")}`;
}

function SubmissionRow({
  reviewId,
  name,
  photoUrl,
  submittedLabel,
  to,
}: {
  reviewId: string;
  name: string;
  photoUrl: string | null;
  submittedLabel: string | null;
  to:
    | "/users/mentor-profile-reviews/$reviewId"
    | "/users/mentor-story-reviews/$reviewId";
}) {
  const signed = useSignedMedia(photoUrl);

  return (
    <li className="flex items-center justify-between gap-4 border-b border-surfaces-divider px-7 py-5 last:border-b-0">
      <div className="flex min-w-0 items-center gap-3">
        <Avatar className="size-12 rounded-xl">
          {signed.url ? <AvatarImage src={signed.url} alt={name} /> : null}
          <AvatarFallback className="rounded-xl text-sm">
            {getInitials(name)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="truncate font-heading text-base font-bold leading-4 text-text-primary">
            {name}
          </p>
          {submittedLabel ? (
            <p className="mt-2 font-body text-xs leading-[1.6] text-text-primary/75">
              {submittedLabel}
            </p>
          ) : null}
        </div>
      </div>
      <Link to={to} params={{ reviewId }}>
        <Button size="sm" fullWidth={false}>
          Review
        </Button>
      </Link>
    </li>
  );
}

const PENDING_QUEUE_STATUSES = new Set(["submitted", "in_review"]);

export function UsersActiveReviewStrip() {
  const ability = useAbility();
  const canRead = ability.can("read", "mentor-profile-reviews");
  const storiesEnabled = useMentorStoriesEnabled();
  const { data: reviews, isPending } = useMentorProfileReviewsQueue(canRead);
  const { data: storyReviews, isPending: storiesPending } =
    useStoryReviewQueue(canRead && storiesEnabled);
  const [open, setOpen] = useState(true);

  const pendingReviews = (reviews ?? []).filter((review) =>
    PENDING_QUEUE_STATUSES.has(review.status),
  );
  const pendingStories = storiesEnabled
    ? (storyReviews ?? []).filter((review) => review.status === "in_review")
    : [];
  const total = pendingReviews.length + pendingStories.length;

  if (!canRead) return null;
  if (isPending || (storiesEnabled && storiesPending)) {
    return (
      <div className="mb-6 flex items-center gap-2 rounded-xl border border-surfaces-divider bg-surfaces-z0 px-6 py-3.5 text-sm text-text-secondary">
        <Loader2 className="size-4 animate-spin" />
        Checking new mentor submissions…
      </div>
    );
  }
  if (!total) return null;

  return (
    <section className="mb-6 overflow-hidden rounded-xl border border-surfaces-divider bg-surfaces-z0">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between px-6 py-3.5 text-left"
      >
        <span className="flex items-center gap-3">
          <ChevronDown
            className={cn(
              "size-5 shrink-0 text-text-primary transition-transform",
              !open && "-rotate-90",
            )}
            strokeWidth={2}
          />
          <span className="font-body text-sm font-medium leading-4 text-text-primary">
            New Mentor Submission
          </span>
        </span>
        <span className="font-mono text-sm font-medium leading-4 text-text-primary/50">
          {total}
        </span>
      </button>

      {open ? (
        <ul className="border-t border-surfaces-divider">
          {pendingReviews.map((review) => (
            <SubmissionRow
              key={review.id}
              reviewId={review.id}
              name={mentorLabel(review)}
              photoUrl={mentorAvatarUrl(review)}
              submittedLabel={formatSubmitted(
                review.submitted_at ?? review.created_at,
              )}
              to="/users/mentor-profile-reviews/$reviewId"
            />
          ))}
          {pendingStories.map((review) => (
            <StorySubmissionRow key={review.id} review={review} />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function StorySubmissionRow({ review }: { review: StoryReview }) {
  const { data: mentor } = useUser(review.userId);
  const name = (mentor ? getPreferredName(mentor) : "") || "Mentor";
  const submitted = formatSubmitted(review.submittedAt);

  return (
    <SubmissionRow
      reviewId={review.id}
      name={name}
      photoUrl={mentor?.profile_picture_url ?? null}
      submittedLabel={
        submitted ? `Mentor story • ${submitted}` : "Mentor story"
      }
      to="/users/mentor-story-reviews/$reviewId"
    />
  );
}
